import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Gift,
  ArrowLeft,
  Loader2,
  AlertCircle,
  CheckCircle,
  Download,
  Mail,
  Phone,
  MessageCircle,
  MapPin,
  Clock,
  Globe,
  DollarSign,
  FileText,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  Palmtree,
  Bus,
  Hotel,
  Tent,
  GraduationCap,
  Waves,
  Shirt,
  Dumbbell,
  Copy,
  Info,
  Sparkles,
  Share2,
  Send,
  BookOpen,
  Ticket,
  Lock,
  KeyRound,
  HelpCircle,
  Store,
} from 'lucide-react';
import { UserHeader } from '../components/UserHeader';
import UnlockBeneficiosModal from '../components/beneficios/UnlockBeneficiosModal';
import { goBack } from '../utils/navigation';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import remarkBreaks from 'remark-breaks';
import remarkGfm from 'remark-gfm';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const MarkdownText = ({ children, className = 'text-gray-700' }) => (
  <ReactMarkdown
    remarkPlugins={[remarkBreaks, remarkGfm]}
    components={{
      p: ({ children }) => <p className={`${className} leading-relaxed mb-2`}>{children}</p>,
      strong: ({ children }) => <strong className="font-semibold text-gray-900">{children}</strong>,
      em: ({ children }) => <em className="italic">{children}</em>,
      ul: ({ children }) => <ul className="list-disc list-inside space-y-1 mb-3 ml-2">{children}</ul>,
      ol: ({ children }) => <ol className="list-decimal list-inside space-y-1 mb-3 ml-2">{children}</ol>,
      li: ({ children }) => <li className={className}>{children}</li>,
      br: () => <br />,
      table: ({ children }) => <table className="w-full border-collapse my-3 text-sm">{children}</table>,
      thead: ({ children }) => <thead className="bg-gray-100">{children}</thead>,
      tbody: ({ children }) => <tbody>{children}</tbody>,
      tr: ({ children }) => <tr className="border-b border-gray-200">{children}</tr>,
      th: ({ children }) => <th className="px-3 py-2 text-left font-semibold text-gray-900 border border-gray-300">{children}</th>,
      td: ({ children }) => <td className="px-3 py-2 text-gray-700 border border-gray-300">{children}</td>,
    }}
  >
    {children}
  </ReactMarkdown>
);

const iconMap = {
  Palmtree,
  Bus,
  Hotel,
  Tent,
  GraduationCap,
  Waves,
  Shirt,
  Dumbbell,
};

// Acordeón manual (mismo look que Cuponstar)
const AcordeonSeccion = ({ titulo, children, defaultOpen = false, testId, icon }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border-t border-gray-200" data-testid={testId}>
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between py-4 text-left hover:bg-gray-50 transition-colors"
        aria-expanded={open}
      >
        <div className="flex items-center gap-2">
          {icon}
          <span className="font-semibold text-gray-900">{titulo}</span>
        </div>
        <ChevronDown className={`w-5 h-5 text-gray-500 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && <div className="pb-5 pt-1">{children}</div>}
    </div>
  );
};

// Card de paquete turístico (acordeón expandible con imagen + descripción larga + CTA)
const PaqueteCard = ({ paquete, index, onGenerarVoucher, generandoVoucher, datosUsuario, navigate, sinAcceso, onUnlock }) => {
  const [abierto, setAbierto] = React.useState(false);

  const handleAccion = () => {
    if (sinAcceso) {
      if (onUnlock) onUnlock(`reservar el paquete ${paquete.titulo || ''}`);
      return;
    }
    if (!datosUsuario) {
      navigate('/beneficios');
      return;
    }
    const tipo = paquete.boton_tipo;
    const cfg = paquete.boton_config || {};
    if (tipo === 'voucher') {
      onGenerarVoucher();
    } else if (tipo === 'whatsapp') {
      const numero = (cfg.numero || '').replace(/\D/g, '');
      const mensaje = cfg.mensaje ? `?text=${encodeURIComponent(cfg.mensaje)}` : '';
      window.open(`https://wa.me/${numero}${mensaje}`, '_blank');
    } else if (tipo === 'email_beneficios') {
      window.location.href = `mailto:beneficios@colegiotslomas.org?subject=${encodeURIComponent('Consulta paquete: ' + (paquete.titulo || ''))}`;
    } else if (tipo === 'web_prestador') {
      const url = cfg.url || '';
      window.open(url.startsWith('http') ? url : `https://${url}`, '_blank');
    }
  };

  const labelBoton = {
    voucher: 'Generar voucher',
    whatsapp: 'Consultar por WhatsApp',
    email_beneficios: 'Enviar mail al Colegio',
    web_prestador: 'Ir al sitio del prestador',
  }[paquete.boton_tipo];

  return (
    <div
      className="border border-gray-200 rounded-2xl overflow-hidden bg-white hover:shadow-md transition-shadow"
      data-testid={`paquete-card-${index}`}
    >
      {/* Header clickeable */}
      <button
        onClick={() => setAbierto((v) => !v)}
        className="w-full flex items-stretch text-left hover:bg-gray-50 transition-colors"
        aria-expanded={abierto}
        data-testid={`paquete-toggle-${index}`}
      >
        {/* Thumbnail */}
        <div className="w-24 sm:w-32 flex-shrink-0 bg-gradient-to-br from-purple-100 to-purple-200 relative">
          {paquete.imagen_url ? (
            <img
              src={paquete.imagen_url}
              alt={paquete.titulo}
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <Ticket className="w-8 h-8 text-purple-400" />
            </div>
          )}
        </div>
        {/* Info */}
        <div className="flex-1 p-4 min-w-0">
          <p className="font-bold text-gray-900 leading-tight line-clamp-2">
            {paquete.titulo || 'Paquete sin título'}
          </p>
          {paquete.descripcion_corta && (
            <p className="text-sm text-gray-600 mt-1 line-clamp-2">{paquete.descripcion_corta}</p>
          )}
          <div className="flex flex-wrap items-center gap-2 mt-2">
            {paquete.precio && (
              <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-full">
                <DollarSign className="w-3 h-3" />
                {paquete.precio}
              </span>
            )}
            {paquete.vigencia && (
              <span className="inline-flex items-center gap-1 text-xs text-purple-700 bg-purple-50 border border-purple-200 px-2 py-0.5 rounded-full">
                <Clock className="w-3 h-3" />
                {paquete.vigencia}
              </span>
            )}
          </div>
        </div>
        {/* Chevron */}
        <div className="flex items-center pr-4">
          <ChevronDown
            className={`w-5 h-5 text-gray-500 transition-transform ${abierto ? 'rotate-180' : ''}`}
          />
        </div>
      </button>

      {/* Cuerpo expandido */}
      {abierto && (
        <div className="border-t border-gray-100 p-4 sm:p-5 bg-gray-50/40">
          {paquete.imagen_url && (
            <img
              src={paquete.imagen_url}
              alt={paquete.titulo}
              className="w-full max-h-64 object-cover rounded-lg mb-4 border border-gray-200"
            />
          )}
          {paquete.descripcion_larga && (
            <div className="prose prose-purple max-w-none text-gray-700 mb-4">
              <MarkdownText>{paquete.descripcion_larga}</MarkdownText>
            </div>
          )}
          {paquete.boton_tipo && paquete.boton_tipo !== 'ninguno' && (
            <Button
              size="lg"
              className="w-full sm:w-auto bg-gray-900 hover:bg-black text-white font-semibold rounded-xl"
              onClick={handleAccion}
              disabled={generandoVoucher && paquete.boton_tipo === 'voucher' && !sinAcceso}
              data-testid={`paquete-cta-${index}`}
            >
              {sinAcceso ? (
                <>
                  <Lock className="w-4 h-4 mr-2" />
                  Verificar matrícula para reservar
                </>
              ) : generandoVoucher && paquete.boton_tipo === 'voucher' ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Generando...
                </>
              ) : (
                <>
                  {paquete.boton_tipo === 'voucher' && <Download className="w-4 h-4 mr-2" />}
                  {paquete.boton_tipo === 'whatsapp' && <MessageCircle className="w-4 h-4 mr-2" />}
                  {paquete.boton_tipo === 'email_beneficios' && <Mail className="w-4 h-4 mr-2" />}
                  {paquete.boton_tipo === 'web_prestador' && <Globe className="w-4 h-4 mr-2" />}
                  {labelBoton}
                </>
              )}
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

// Card de prestador relacionado (estilo Cuponstar: imagen grande + logo circular)
const PrestadorRelacionadoCard = ({ prestador, categoriaIcon, onClick }) => {
  const Icon = categoriaIcon || Gift;
  const heroImg = prestador.imagen_principal || (prestador.imagenes && prestador.imagenes[0]);
  return (
    <button
      onClick={onClick}
      className="group w-full bg-white rounded-2xl border border-gray-200 hover:border-purple-400 hover:shadow-lg transition-all text-left overflow-hidden"
      data-testid={`prestador-relacionado-${prestador.id}`}
    >
      {/* Imagen de fondo con overlay + logo circular */}
      <div className="relative aspect-[4/3] bg-gradient-to-br from-purple-100 via-purple-50 to-white overflow-hidden">
        {heroImg ? (
          <>
            <img
              src={heroImg}
              alt={prestador.nombre}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent" />
          </>
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Icon className="w-16 h-16 text-purple-300" />
          </div>
        )}
        {/* Logo circular flotante */}
        <div className="absolute inset-x-0 bottom-0 flex justify-center translate-y-1/2">
          <div className="w-16 h-16 bg-white rounded-2xl shadow-md ring-4 ring-white flex items-center justify-center overflow-hidden">
            {prestador.logo_url ? (
              <img src={prestador.logo_url} alt={prestador.nombre} className="max-w-full max-h-full object-contain p-1.5" />
            ) : (
              <Icon className="w-8 h-8 text-purple-400" />
            )}
          </div>
        </div>
      </div>
      <div className="pt-10 pb-4 px-3 text-center">
        <p className="font-bold text-gray-900 text-sm uppercase tracking-tight line-clamp-1">{prestador.nombre}</p>
        <p className="text-xs text-purple-700 mt-1 line-clamp-2 leading-snug">{prestador.beneficio}</p>
      </div>
    </button>
  );
};

export default function PrestadorDetallePage() {
  const { prestadorId } = useParams();
  const navigate = useNavigate();
  const [prestador, setPrestador] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [imagenActual, setImagenActual] = useState(0);
  const [generandoVoucher, setGenerandoVoucher] = useState(false);
  const [relacionados, setRelacionados] = useState([]);

  const [showCodigoModal, setShowCodigoModal] = useState(false);
  const [codigoDescuento, setCodigoDescuento] = useState(null);
  const [codigoInstrucciones, setCodigoInstrucciones] = useState(null);
  const [vinculando, setVinculando] = useState(false);
  const [codigoCopied, setCodigoCopied] = useState(false);

  const [showShareModal, setShowShareModal] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);

  const [datosUsuario, setDatosUsuario] = useState(null);

  // Estado de acceso: sinAcceso === true si es público o si es matriculado no al día
  const sinAcceso = !datosUsuario || datosUsuario.al_dia === false;

  // Modal para desbloquear (verificar matrícula)
  const [showUnlockModal, setShowUnlockModal] = useState(false);
  const [motivoUnlock, setMotivoUnlock] = useState('usar este beneficio');

  const abrirUnlock = (motivo = 'usar este beneficio') => {
    setMotivoUnlock(motivo);
    setShowUnlockModal(true);
  };

  useEffect(() => {
    cargarPrestador();
    const datos = sessionStorage.getItem('beneficios_usuario');
    if (datos) {
      setDatosUsuario(JSON.parse(datos));
    }
    setImagenActual(0);
    window.scrollTo({ top: 0 });
  }, [prestadorId]);

  const cargarPrestador = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/beneficios/prestadores/${prestadorId}`);
      setPrestador(response.data);
      if (response.data?.categoria_id) {
        try {
          const rel = await axios.get(`${API_URL}/beneficios/prestadores`, {
            params: { categoria_id: response.data.categoria_id },
          });
          const filtrados = (rel.data || []).filter((p) => p.id !== response.data.id).slice(0, 5);
          setRelacionados(filtrados);
        } catch {
          setRelacionados([]);
        }
      }
    } catch (err) {
      setError('No se pudo cargar la información del prestador');
    } finally {
      setLoading(false);
    }
  };

  const generarVoucher = async () => {
    if (!datosUsuario) {
      navigate('/beneficios');
      return;
    }
    setGenerandoVoucher(true);
    setError(null);
    try {
      const response = await axios.post(
        `${API_URL}/beneficios/generar-voucher`,
        {
          matricula: datosUsuario.matricula,
          documento: datosUsuario.documento,
          prestador_id: prestadorId,
        },
        { responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `voucher_${prestador?.nombre?.replace(/\s+/g, '_')}_${datosUsuario.matricula}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError('Error al generar el voucher. Verificá que estés al día con tus obligaciones.');
    } finally {
      setGenerandoVoucher(false);
    }
  };

  const mostrarCodigoDescuento = async (config) => {
    if (!datosUsuario) {
      navigate('/beneficios');
      return;
    }
    setVinculando(true);
    setCodigoDescuento(null);
    setCodigoInstrucciones(null);
    setCodigoCopied(false);
    await new Promise((resolve) => setTimeout(resolve, 1500));
    setCodigoDescuento(config?.codigo || 'DESCUENTO');
    setCodigoInstrucciones(config?.instrucciones || '');
    setVinculando(false);
    setShowCodigoModal(true);
  };

  const copiarCodigo = () => {
    if (codigoDescuento) {
      navigator.clipboard.writeText(codigoDescuento);
      setCodigoCopied(true);
      setTimeout(() => setCodigoCopied(false), 2000);
    }
  };

  const compartirWhatsApp = () => {
    if (!prestador) return;
    const texto = `¡Mirá este beneficio en la Guía TS+!\n\n${prestador.nombre}\n${prestador.beneficio || ''}\n\n${window.location.href}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(texto)}`, '_blank');
  };

  const copiarLinkPrestador = () => {
    navigator.clipboard.writeText(window.location.href);
    setLinkCopied(true);
    setTimeout(() => setLinkCopied(false), 2000);
  };

  const siguienteImagen = () => {
    if (prestador?.imagenes?.length > 0) {
      setImagenActual((prev) => (prev + 1) % prestador.imagenes.length);
    }
  };

  const anteriorImagen = () => {
    if (prestador?.imagenes?.length > 0) {
      setImagenActual((prev) => (prev - 1 + prestador.imagenes.length) % prestador.imagenes.length);
    }
  };

  // Render de un botón/CTA — usados en el sidebar
  const renderBotonSidebar = (tipo, config, index) => {
    if (!tipo) return null;
    const isPrimary = index === 0;

    switch (tipo) {
      case 'voucher':
        return (
          <Button
            key={`btn-${index}`}
            size="lg"
            className={
              isPrimary
                ? 'w-full bg-gray-900 hover:bg-black text-white py-6 text-base font-semibold rounded-xl'
                : 'w-full bg-white hover:bg-gray-50 text-gray-900 border-2 border-gray-900 py-5 text-sm font-semibold rounded-xl'
            }
            onClick={generarVoucher}
            disabled={generandoVoucher}
            data-testid={`boton-voucher-${index}`}
          >
            {generandoVoucher ? (
              <>
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                Generando voucher...
              </>
            ) : (
              <>
                <Download className="w-5 h-5 mr-2" />
                {isPrimary ? 'Quiero este beneficio' : 'Generar voucher'}
              </>
            )}
          </Button>
        );

      case 'recreo':
        return (
          <div
            key={`btn-${index}`}
            className="w-full p-3 text-sm border border-green-300 text-green-800 bg-green-50 rounded-xl"
            data-testid={`boton-recreo-${index}`}
          >
            <p className="font-medium flex items-center gap-2 mb-1">
              <Info className="w-4 h-4" />
              Presentarte en el Recreo con:
            </p>
            <p className="text-xs">DNI + Credencial + Voucher (y DNI del grupo familiar si corresponde)</p>
          </div>
        );

      case 'presentarse_lugar':
        return (
          <div
            key={`btn-${index}`}
            className="w-full p-3 text-sm border border-blue-300 text-blue-800 bg-blue-50 rounded-xl"
            data-testid={`boton-presentarse-lugar-${index}`}
          >
            <p className="font-medium flex items-center gap-2 mb-1">
              <Info className="w-4 h-4" />
              Presentarte en el lugar con:
            </p>
            <p className="text-xs">DNI + Credencial + Voucher (y DNI del grupo familiar si corresponde)</p>
          </div>
        );

      case 'codigo_descuento':
        return (
          <Button
            key={`btn-${index}`}
            size="lg"
            className={
              isPrimary
                ? 'w-full bg-gray-900 hover:bg-black text-white py-6 text-base font-semibold rounded-xl'
                : 'w-full bg-white hover:bg-gray-50 text-purple-700 border-2 border-purple-600 py-5 text-sm font-semibold rounded-xl'
            }
            onClick={() => mostrarCodigoDescuento(config)}
            disabled={vinculando}
            data-testid={`boton-codigo-${index}`}
          >
            {vinculando ? (
              <>
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                Vinculando...
              </>
            ) : (
              <>
                <Gift className="w-5 h-5 mr-2" />
                {isPrimary ? 'Quiero este beneficio' : 'Obtener código'}
              </>
            )}
          </Button>
        );

      case 'whatsapp': {
        const numero = config?.numero?.replace(/\D/g, '') || '';
        return (
          <Button
            key={`btn-${index}`}
            variant="outline"
            className="w-full py-4 text-sm border-green-500 text-green-700 hover:bg-green-50 rounded-xl"
            onClick={() => window.open(`https://wa.me/${numero}`, '_blank')}
            data-testid={`boton-whatsapp-${index}`}
          >
            <MessageCircle className="w-4 h-4 mr-2" />
            Comunicarse por WhatsApp
          </Button>
        );
      }

      case 'email_beneficios':
        return (
          <Button
            key={`btn-${index}`}
            variant="outline"
            className="w-full py-4 text-sm border-blue-500 text-blue-700 hover:bg-blue-50 rounded-xl"
            onClick={() => (window.location.href = 'mailto:beneficios@colegiotslomas.org')}
            data-testid={`boton-email-${index}`}
          >
            <Mail className="w-4 h-4 mr-2" />
            Escribir a beneficios
          </Button>
        );

      case 'web_prestador': {
        const url = config?.url || '';
        const urlFinal = url.startsWith('http') ? url : `https://${url}`;
        return (
          <Button
            key={`btn-${index}`}
            variant="outline"
            className="w-full py-4 text-sm border-purple-500 text-purple-700 hover:bg-purple-50 rounded-xl"
            onClick={() => window.open(urlFinal, '_blank')}
            data-testid={`boton-web-${index}`}
          >
            <Globe className="w-4 h-4 mr-2" />
            Ir al sitio web
          </Button>
        );
      }

      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#f3f0fd]">
        <UserHeader />
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
        </div>
      </div>
    );
  }

  if (error && !prestador) {
    return (
      <div className="min-h-screen bg-[#f3f0fd]">
        <UserHeader />
        <main className="max-w-2xl mx-auto px-4 py-8">
          <Button variant="ghost" onClick={() => goBack()} className="mb-4">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Volver
          </Button>
          <Card className="border-red-200">
            <CardContent className="py-8 text-center">
              <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
              <p className="text-gray-600">{error}</p>
            </CardContent>
          </Card>
        </main>
      </div>
    );
  }

  const CategoriaIcon = iconMap[prestador?.categoria_icono] || Gift;

  // Imágenes para carrusel principal
  const carruselImgs = [];
  if (prestador?.imagen_principal) carruselImgs.push(prestador.imagen_principal);
  if (prestador?.imagenes && prestador.imagenes.length > 0) {
    prestador.imagenes.forEach((img) => {
      if (!carruselImgs.includes(img)) carruselImgs.push(img);
    });
  }
  const tieneCarrusel = carruselImgs.length > 0;

  const botones = [
    { tipo: prestador?.boton1_tipo, config: prestador?.boton1_config },
    { tipo: prestador?.boton2_tipo, config: prestador?.boton2_config },
    { tipo: prestador?.boton3_tipo, config: prestador?.boton3_config },
  ].filter((b) => b.tipo);

  const direccionCompleta = [prestador?.ubicacion || prestador?.direccion, prestador?.localidad, prestador?.provincia]
    .filter(Boolean)
    .join(', ');
  const googleMapsUrl = direccionCompleta
    ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(direccionCompleta)}`
    : null;

  const tieneUbicacion = !!(prestador?.ubicacion || prestador?.direccion || prestador?.localidad || prestador?.provincia);
  const tieneContacto = !!(prestador?.telefono || prestador?.email || prestador?.whatsapp || prestador?.sitio_web);

  return (
    <div className="min-h-screen bg-[#f5f3fa] pb-28 md:pb-16">
      <UserHeader />

      <main className="max-w-6xl mx-auto px-4 py-6">
        {/* Breadcrumb / Volver */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2 text-xs text-gray-500 uppercase tracking-wider">
            <button onClick={() => navigate('/beneficios')} className="hover:text-purple-700 font-semibold" data-testid="volver-btn">
              CUPONES
            </button>
            <ChevronRight className="w-3 h-3" />
            <span className="text-gray-800">{prestador.nombre}</span>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowShareModal(true)}
            className="border-purple-300 text-purple-700 hover:bg-purple-50"
            data-testid="compartir-btn"
          >
            <Share2 className="w-4 h-4 mr-2" />
            Compartir
          </Button>
        </div>

        {/* ================= GRID 2 columnas (desktop) ================= */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* ===== Columna principal (contenido) ===== */}
          <div className="lg:col-span-2">
            <Card className="overflow-hidden rounded-2xl shadow-sm">
              {/* Hero: carrusel de imágenes */}
              {tieneCarrusel ? (
                <div className="relative aspect-[16/9] bg-gray-100">
                  <img
                    src={carruselImgs[imagenActual]}
                    alt={`${prestador.nombre} - ${imagenActual + 1}`}
                    className="w-full h-full object-cover"
                  />
                  {carruselImgs.length > 1 && (
                    <>
                      <button
                        onClick={anteriorImagen}
                        className="absolute left-3 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white text-gray-800 p-2 rounded-full shadow-md transition-all"
                        aria-label="Imagen anterior"
                        data-testid="carrusel-anterior"
                      >
                        <ChevronLeft className="w-5 h-5" />
                      </button>
                      <button
                        onClick={siguienteImagen}
                        className="absolute right-3 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white text-gray-800 p-2 rounded-full shadow-md transition-all"
                        aria-label="Imagen siguiente"
                        data-testid="carrusel-siguiente"
                      >
                        <ChevronRight className="w-5 h-5" />
                      </button>
                      <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
                        {carruselImgs.map((_, i) => (
                          <button
                            key={i}
                            onClick={() => setImagenActual(i)}
                            className={`h-1.5 rounded-full transition-all ${
                              i === imagenActual ? 'bg-white w-6' : 'bg-white/60 w-1.5'
                            }`}
                            aria-label={`Ir a imagen ${i + 1}`}
                          />
                        ))}
                      </div>
                    </>
                  )}
                  {prestador.origen === 'rueda_turismo' && (
                    <Badge className="absolute top-3 left-3 bg-amber-400 text-amber-950 border-0 shadow">
                      <Sparkles className="w-3 h-3 mr-1" />
                      Rueda de Turismo Social
                    </Badge>
                  )}
                </div>
              ) : (
                <div className="relative aspect-[16/9] bg-gradient-to-br from-purple-400 via-purple-500 to-purple-700 flex items-center justify-center">
                  <CategoriaIcon className="w-20 h-20 text-white/40" />
                  {prestador.origen === 'rueda_turismo' && (
                    <Badge className="absolute top-3 left-3 bg-amber-400 text-amber-950 border-0 shadow">
                      <Sparkles className="w-3 h-3 mr-1" />
                      Rueda de Turismo Social
                    </Badge>
                  )}
                </div>
              )}

              {/* Contenido del card (nombre, descripción, instrucciones, acordeones) */}
              <div className="p-5 sm:p-6">
                {/* Categoría (mini) */}
                <Badge className="bg-purple-100 text-purple-800 border-0 mb-2">
                  <CategoriaIcon className="w-3 h-3 mr-1" />
                  {prestador.categoria_nombre || 'Beneficio'}
                </Badge>

                {/* Nombre */}
                <h1
                  className="text-2xl sm:text-3xl font-bold text-gray-900 mb-3"
                  data-testid="prestador-nombre"
                >
                  {prestador.nombre}
                </h1>

                {/* Descripción principal */}
                {(prestador.descripcion_parrafo1 || prestador.descripcion) && (
                  <div className="prose prose-purple max-w-none text-gray-700 mb-4">
                    <MarkdownText>{prestador.descripcion_parrafo1 || prestador.descripcion}</MarkdownText>
                  </div>
                )}

                {prestador.descripcion_parrafo2 && (
                  <div className="prose prose-purple max-w-none text-gray-700 mb-4">
                    <MarkdownText>{prestador.descripcion_parrafo2}</MarkdownText>
                  </div>
                )}

                {/* Instrucciones de uso — solo visible para matriculados al día */}
                {prestador.instrucciones && !sinAcceso && (
                  <div className="mt-6">
                    <h2 className="font-bold text-gray-900 text-base mb-2">Instrucciones de uso</h2>
                    <div className="prose prose-purple max-w-none text-gray-700">
                      <MarkdownText>{prestador.instrucciones}</MarkdownText>
                    </div>
                  </div>
                )}

                {/* Placeholder locked de Instrucciones para público */}
                {prestador.instrucciones && sinAcceso && (
                  <div className="mt-6 p-4 rounded-xl border-2 border-dashed border-purple-200 bg-purple-50/40 text-center">
                    <div className="w-10 h-10 mx-auto mb-2 rounded-full bg-purple-100 flex items-center justify-center">
                      <Lock className="w-5 h-5 text-purple-600" />
                    </div>
                    <p className="font-semibold text-gray-900 text-sm">Instrucciones de uso disponibles para matriculados al día</p>
                    <p className="text-xs text-gray-600 mt-1">Verificá tu matrícula para ver cómo usar este beneficio.</p>
                    <Button
                      size="sm"
                      onClick={() => abrirUnlock('ver las instrucciones de este beneficio')}
                      className="mt-3 bg-gray-900 hover:bg-black text-white rounded-lg"
                      data-testid="unlock-instrucciones-btn"
                    >
                      <KeyRound className="w-3.5 h-3.5 mr-1" />
                      Verificar mi matrícula
                    </Button>
                  </div>
                )}

                {/* Error banner */}
                {error && (
                  <div className="mt-4 p-3 bg-red-100 border border-red-300 rounded-lg flex items-center gap-2 text-red-700">
                    <AlertCircle className="w-4 h-4" />
                    <span className="text-sm">{error}</span>
                  </div>
                )}

                {/* ==================== PAQUETES DISPONIBLES ==================== */}
                {Array.isArray(prestador.paquetes) && prestador.paquetes.length > 0 && (
                  <div className="mt-8" data-testid="seccion-paquetes">
                    <div className="flex items-center gap-2 mb-4">
                      <Ticket className="w-5 h-5 text-purple-600" />
                      <h2 className="text-lg font-bold text-gray-900">
                        Paquetes disponibles ({prestador.paquetes.length})
                      </h2>
                    </div>
                    <div className="space-y-3">
                      {prestador.paquetes.map((paq, idx) => (
                        <PaqueteCard
                          key={paq.id || idx}
                          paquete={paq}
                          index={idx}
                          onGenerarVoucher={generarVoucher}
                          generandoVoucher={generandoVoucher}
                          datosUsuario={datosUsuario}
                          navigate={navigate}
                          sinAcceso={sinAcceso}
                          onUnlock={(motivo) => abrirUnlock(motivo)}
                        />
                      ))}
                    </div>
                  </div>
                )}

                {/* ==================== ACORDEONES ==================== */}
                <div className="mt-6">
                  {prestador.detalles_beneficio && (
                    <AcordeonSeccion
                      titulo="Detalles del beneficio"
                      icon={<Gift className="w-5 h-5 text-purple-600" />}
                      defaultOpen={true}
                      testId="seccion-detalles-beneficio"
                    >
                      <div className="prose prose-purple max-w-none text-gray-700">
                        <MarkdownText>{prestador.detalles_beneficio}</MarkdownText>
                      </div>
                    </AcordeonSeccion>
                  )}

                  {prestador.condiciones && (
                    <AcordeonSeccion
                      titulo="Bases y condiciones"
                      icon={<FileText className="w-5 h-5 text-amber-600" />}
                      defaultOpen={false}
                      testId="seccion-condiciones"
                    >
                      <div className="prose prose-purple max-w-none text-gray-700">
                        <MarkdownText>{prestador.condiciones}</MarkdownText>
                      </div>
                    </AcordeonSeccion>
                  )}

                  {(prestador.precios_tarifas || prestador.precios) && (
                    <AcordeonSeccion
                      titulo="Precios y tarifas"
                      icon={<DollarSign className="w-5 h-5 text-purple-600" />}
                      defaultOpen={false}
                      testId="seccion-precios"
                    >
                      <div className="prose prose-purple max-w-none text-gray-700 overflow-x-auto">
                        <MarkdownText>{prestador.precios_tarifas || prestador.precios}</MarkdownText>
                      </div>
                    </AcordeonSeccion>
                  )}

                  {prestador.horarios && (
                    <AcordeonSeccion
                      titulo="Horarios"
                      icon={<Clock className="w-5 h-5 text-purple-600" />}
                      defaultOpen={false}
                      testId="seccion-horarios"
                    >
                      <div className="prose prose-purple max-w-none text-gray-700">
                        <MarkdownText>{prestador.horarios}</MarkdownText>
                      </div>
                    </AcordeonSeccion>
                  )}

                  {tieneUbicacion && (
                    <AcordeonSeccion
                      titulo="Ubicación"
                      icon={<MapPin className="w-5 h-5 text-purple-600" />}
                      defaultOpen={false}
                      testId="seccion-ubicacion"
                    >
                      <div className="flex items-start gap-3">
                        <div className="w-9 h-9 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0">
                          <MapPin className="w-4 h-4 text-purple-600" />
                        </div>
                        <div className="flex-1">
                          <p className="font-medium text-gray-900">{prestador.ubicacion || prestador.direccion}</p>
                          {(prestador.localidad || prestador.provincia) && (
                            <p className="text-sm text-gray-600 mt-0.5">
                              {[prestador.localidad, prestador.provincia].filter(Boolean).join(', ')}
                            </p>
                          )}
                          {googleMapsUrl && (
                            <a
                              href={googleMapsUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 text-sm text-purple-700 hover:text-purple-900 hover:underline mt-2 font-medium"
                              data-testid="ver-en-maps-link"
                            >
                              Ver en Google Maps
                              <MapPin className="w-3 h-3" />
                            </a>
                          )}
                        </div>
                      </div>
                    </AcordeonSeccion>
                  )}

                  {tieneContacto && (
                    <AcordeonSeccion
                      titulo="Contacto"
                      icon={<Phone className="w-5 h-5 text-purple-600" />}
                      defaultOpen={false}
                      testId="seccion-contacto"
                    >
                      {sinAcceso ? (
                        <div className="p-4 rounded-xl border-2 border-dashed border-purple-200 bg-purple-50/40 text-center">
                          <div className="w-10 h-10 mx-auto mb-2 rounded-full bg-purple-100 flex items-center justify-center">
                            <Lock className="w-5 h-5 text-purple-600" />
                          </div>
                          <p className="font-semibold text-gray-900 text-sm">Datos de contacto disponibles para matriculados al día</p>
                          <p className="text-xs text-gray-600 mt-1">
                            Teléfono, WhatsApp, email y sitio web del prestador se desbloquean al verificar tu matrícula.
                          </p>
                          <Button
                            size="sm"
                            onClick={() => abrirUnlock('ver los datos de contacto')}
                            className="mt-3 bg-gray-900 hover:bg-black text-white rounded-lg"
                            data-testid="unlock-contacto-btn"
                          >
                            <KeyRound className="w-3.5 h-3.5 mr-1" />
                            Verificar mi matrícula
                          </Button>
                        </div>
                      ) : (
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {prestador.telefono && (
                          <a
                            href={`tel:${prestador.telefono}`}
                            className="flex items-center gap-3 p-3 border border-gray-200 rounded-xl hover:border-purple-300 hover:bg-purple-50/50 transition-colors"
                          >
                            <Phone className="w-4 h-4 text-purple-600 flex-shrink-0" />
                            <div className="min-w-0">
                              <p className="text-xs text-gray-500">Teléfono</p>
                              <p className="font-medium text-gray-900 truncate text-sm">{prestador.telefono}</p>
                            </div>
                          </a>
                        )}
                        {prestador.email && (
                          <a
                            href={`mailto:${prestador.email}`}
                            className="flex items-center gap-3 p-3 border border-gray-200 rounded-xl hover:border-purple-300 hover:bg-purple-50/50 transition-colors"
                          >
                            <Mail className="w-4 h-4 text-purple-600 flex-shrink-0" />
                            <div className="min-w-0">
                              <p className="text-xs text-gray-500">Email</p>
                              <p className="font-medium text-gray-900 truncate text-sm">{prestador.email}</p>
                            </div>
                          </a>
                        )}
                        {prestador.whatsapp && (
                          <a
                            href={`https://wa.me/${prestador.whatsapp.replace(/\D/g, '')}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-3 p-3 border border-gray-200 rounded-xl hover:border-green-400 hover:bg-green-50 transition-colors"
                          >
                            <MessageCircle className="w-4 h-4 text-green-600 flex-shrink-0" />
                            <div className="min-w-0">
                              <p className="text-xs text-gray-500">WhatsApp</p>
                              <p className="font-medium text-gray-900 truncate text-sm">{prestador.whatsapp}</p>
                            </div>
                          </a>
                        )}
                        {prestador.sitio_web && (
                          <a
                            href={prestador.sitio_web.startsWith('http') ? prestador.sitio_web : `https://${prestador.sitio_web}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-3 p-3 border border-gray-200 rounded-xl hover:border-purple-300 hover:bg-purple-50/50 transition-colors"
                          >
                            <Globe className="w-4 h-4 text-purple-600 flex-shrink-0" />
                            <div className="min-w-0">
                              <p className="text-xs text-gray-500">Sitio web</p>
                              <p className="font-medium text-gray-900 truncate text-sm">{prestador.sitio_web}</p>
                            </div>
                          </a>
                        )}
                      </div>
                      )}
                    </AcordeonSeccion>
                  )}

                  {prestador.descripcion_larga &&
                    !prestador.descripcion_parrafo1 &&
                    !prestador.descripcion_parrafo2 && (
                      <AcordeonSeccion
                        titulo={`Sobre ${prestador.nombre}`}
                        icon={<BookOpen className="w-5 h-5 text-purple-600" />}
                        defaultOpen={false}
                        testId="seccion-sobre-prestador"
                      >
                        <div className="prose prose-purple max-w-none text-gray-700">
                          <MarkdownText>{prestador.descripcion_larga}</MarkdownText>
                        </div>
                      </AcordeonSeccion>
                    )}
                </div>
              </div>
            </Card>
          </div>

          {/* ===== Sidebar (CTA sticky) ===== */}
          <aside className="lg:col-span-1 space-y-4">
            <div className="lg:sticky lg:top-4 space-y-4">
              {/* Card 1: Logo + Nombre + Beneficio */}
              <Card className="rounded-2xl overflow-hidden shadow-sm">
                <div className="p-5 flex items-start gap-3">
                  <div className="w-16 h-16 bg-white rounded-xl border border-gray-200 flex items-center justify-center flex-shrink-0 overflow-hidden">
                    {prestador.logo_url ? (
                      <img
                        src={prestador.logo_url}
                        alt={prestador.nombre}
                        className="max-w-full max-h-full object-contain p-1.5"
                      />
                    ) : (
                      <CategoriaIcon className="w-8 h-8 text-purple-400" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-[10px] font-semibold text-purple-700 uppercase tracking-wider">
                      Beneficio en
                    </p>
                    <p className="font-bold text-gray-900 text-lg leading-tight line-clamp-2">
                      {prestador.nombre}
                    </p>
                  </div>
                </div>
                {prestador.beneficio && (
                  <div className="border-t border-gray-100 bg-gradient-to-br from-purple-50 to-purple-100/50 p-4">
                    <p className="text-sm text-gray-900 font-medium leading-snug">{prestador.beneficio}</p>
                  </div>
                )}
              </Card>

              {/* Card 2: CTA principal + botones */}
              <Card className="rounded-2xl overflow-hidden shadow-sm">
                <div className="p-5 space-y-3">
                  {/* Tag "Usa este cupón" */}
                  <div className="flex items-center gap-2 text-xs text-gray-600">
                    <Ticket className="w-4 h-4 text-purple-600" />
                    <span className="font-medium">
                      {prestador.titulo_seccion_botones === 'links_interes' ? 'Enlaces útiles' : 'Usá este beneficio'}
                    </span>
                    {prestador.origen === 'rueda_turismo' && (
                      <Badge variant="outline" className="ml-auto text-[10px] py-0 h-5">
                        <Store className="w-2.5 h-2.5 mr-1" />
                        Presencial
                      </Badge>
                    )}
                  </div>

                  {/* CTA principal */}
                  {sinAcceso ? (
                    <div className="space-y-2">
                      <div className="p-4 rounded-xl bg-gradient-to-br from-purple-50 to-purple-100/60 border border-purple-200 flex items-start gap-3">
                        <div className="w-9 h-9 rounded-full bg-gradient-to-br from-purple-500 to-purple-700 flex items-center justify-center flex-shrink-0">
                          <Lock className="w-4 h-4 text-white" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-gray-900 leading-tight">
                            {datosUsuario?.al_dia === false ? 'Necesitás estar al día' : 'Beneficio exclusivo'}
                          </p>
                          <p className="text-xs text-gray-600 mt-0.5 leading-snug">
                            {datosUsuario?.al_dia === false
                              ? 'Regularizá tu situación para acceder a este beneficio.'
                              : 'Verificá tu matrícula para usar este beneficio.'}
                          </p>
                        </div>
                      </div>

                      <Button
                        size="lg"
                        onClick={() => abrirUnlock(`usar el beneficio de ${prestador.nombre}`)}
                        className="w-full bg-gray-900 hover:bg-black text-white py-6 text-base font-semibold rounded-xl"
                        data-testid="sidebar-verificar-btn"
                      >
                        <KeyRound className="w-4 h-4 mr-2" />
                        {datosUsuario?.al_dia === false ? 'Verificar de nuevo' : 'Ya estoy al día · Verificar matrícula'}
                      </Button>
                      <a
                        href="https://servicios.colegiotslomas.org/estado-cuenta"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <Button
                          size="lg"
                          variant="outline"
                          className="w-full py-6 text-base font-medium rounded-xl border-purple-200 text-purple-700 hover:bg-purple-50"
                          data-testid="sidebar-como-al-dia-btn"
                        >
                          <HelpCircle className="w-4 h-4 mr-2" />
                          Cómo ponerme al día
                        </Button>
                      </a>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {botones.length > 0 ? (
                        botones.map((b, idx) => renderBotonSidebar(b.tipo, b.config, idx))
                      ) : (
                        <Button
                          size="lg"
                          className="w-full bg-gray-900 hover:bg-black text-white py-6 text-base font-semibold rounded-xl"
                          onClick={generarVoucher}
                          disabled={generandoVoucher}
                          data-testid="generar-voucher-btn"
                        >
                          {generandoVoucher ? (
                            <>
                              <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                              Generando voucher...
                            </>
                          ) : (
                            <>
                              <Download className="w-5 h-5 mr-2" />
                              Quiero este beneficio
                            </>
                          )}
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              </Card>

              {/* Card 3: Footer contacto (mini) */}
              <div className="p-4 bg-white/80 border border-gray-200 rounded-2xl">
                <p className="text-xs text-gray-600 text-center">
                  ¿Dudas? Escribinos a{' '}
                  <a
                    href="mailto:beneficios@colegiotslomas.org"
                    className="text-purple-600 hover:text-purple-700 font-medium hover:underline"
                  >
                    beneficios@colegiotslomas.org
                  </a>
                </p>
              </div>
            </div>
          </aside>
        </div>

        {/* ================= PRESTADORES RELACIONADOS ================= */}
        {relacionados.length > 0 && (
          <section className="mt-10" data-testid="prestadores-relacionados-section">
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-5">Descubrí más beneficios</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4">
              {relacionados.map((p) => (
                <PrestadorRelacionadoCard
                  key={p.id}
                  prestador={p}
                  categoriaIcon={iconMap[prestador.categoria_icono]}
                  onClick={() => navigate(`/beneficios/prestador/${p.id}`)}
                />
              ))}
            </div>
          </section>
        )}
      </main>

      {/* ================= Sticky CTA mobile ================= */}
      <div className="fixed bottom-0 left-0 right-0 lg:hidden bg-white border-t border-gray-200 shadow-2xl p-3 z-40">
        {sinAcceso ? (
          <Button
            size="lg"
            className="w-full bg-gray-900 hover:bg-black text-white py-6 text-base font-semibold rounded-xl shadow-lg"
            onClick={() => abrirUnlock(`usar el beneficio de ${prestador.nombre}`)}
            data-testid="sticky-cta-unlock"
          >
            <KeyRound className="w-5 h-5 mr-2" />
            Verificar mi matrícula
          </Button>
        ) : (
          <Button
            size="lg"
            className="w-full bg-gray-900 hover:bg-black text-white py-6 text-base font-semibold rounded-xl shadow-lg"
            onClick={generarVoucher}
            disabled={generandoVoucher}
            data-testid="sticky-cta-voucher"
          >
            {generandoVoucher ? (
              <>
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                Generando voucher...
              </>
            ) : (
              <>
                <Download className="w-5 h-5 mr-2" />
                Quiero este beneficio
              </>
            )}
          </Button>
        )}
      </div>

      {/* Modal para desbloquear (verificar matrícula) */}
      <UnlockBeneficiosModal
        open={showUnlockModal}
        onOpenChange={setShowUnlockModal}
        onUnlocked={(u) => setDatosUsuario(u)}
        motivo={motivoUnlock}
      />

      {/* Modal Compartir */}
      <Dialog open={showShareModal} onOpenChange={setShowShareModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Share2 className="w-5 h-5 text-purple-600" />
              Compartir beneficio
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <Button
              onClick={compartirWhatsApp}
              className="w-full bg-green-600 hover:bg-green-700 py-6"
              data-testid="compartir-whatsapp-btn"
            >
              <Send className="w-5 h-5 mr-2" />
              Compartir por WhatsApp
            </Button>
            <Button
              onClick={copiarLinkPrestador}
              variant="outline"
              className="w-full py-6"
              data-testid="copiar-link-btn"
            >
              {linkCopied ? (
                <>
                  <CheckCircle className="w-5 h-5 mr-2 text-green-600" />
                  ¡Link copiado!
                </>
              ) : (
                <>
                  <Copy className="w-5 h-5 mr-2" />
                  Copiar link
                </>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal Código de Descuento */}
      <Dialog open={showCodigoModal} onOpenChange={setShowCodigoModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-green-600" />
              ¡Código generado correctamente!
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div className="bg-gray-50 rounded-lg p-4 space-y-2">
              <p className="text-sm text-gray-600">
                <span className="font-medium">Usuario:</span> {datosUsuario?.nombre}
              </p>
              <p className="text-sm text-gray-600">
                <span className="font-medium">Matrícula:</span> {datosUsuario?.matricula}
              </p>
              <p className="text-sm text-gray-600">
                <span className="font-medium">Prestador:</span> {prestador?.nombre}
              </p>
            </div>

            <div className="bg-purple-50 border-2 border-purple-200 rounded-lg p-6 text-center">
              <p className="text-sm text-purple-700 mb-2">Tu código de descuento es:</p>
              <p className="text-3xl font-bold text-purple-800 tracking-wider font-mono">{codigoDescuento}</p>
            </div>

            {codigoInstrucciones && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                <p className="text-sm text-amber-800 whitespace-pre-line">{codigoInstrucciones}</p>
              </div>
            )}

            <Button className="w-full" onClick={copiarCodigo}>
              {codigoCopied ? (
                <>
                  <CheckCircle className="w-4 h-4 mr-2" />
                  ¡Código copiado!
                </>
              ) : (
                <>
                  <Copy className="w-4 h-4 mr-2" />
                  Copiar código
                </>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
